import { queryOptions } from "@tanstack/react-query";
import type { WorkspaceTemplateService } from "@/services/workspace-template.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * Workspace templates (NG-2799): the templates a workspace can be seeded or
 * reset from, and one template's retained versions.
 *
 * - **list**, `GET {ws}/templates/`, `[slug, uid, 'workspace-templates']`: read
 *   by Settings › General's templates section and the reset dialog's picker.
 *   Server order is the caller's own last-applied first, so the list is
 *   per-principal by nature, which the key already is.
 * - **versions**, `GET {ws}/templates/{id}/versions/`,
 *   `[slug, uid, 'workspace-template', id, 'versions']`: the reset dialog's
 *   "reset from a past version" picker.
 *
 * Both are **reference** (contract §2), and plain GETs (`"safe-read"`).
 *
 * @writers `WorkspaceTemplatesStore`'s `create`, `remove` and `setDefault`
 * edit the cached list in place (prepend or replace, filter, single default),
 * so the list never re-reads after this tab's write; a remove also drops the
 * template's versions. Another admin's edit is seen
 * when the entry goes stale, until the backend hint lands (NG-2799 PR 6).
 */

export function workspaceTemplatesKey(scope: QueryScope) {
  return scopedKey(scope, "workspace-templates");
}

export function workspaceTemplatesQuery(
  service: Pick<WorkspaceTemplateService, "list">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: workspaceTemplatesKey(scope),
    queryFn: scopedRead((request) => service.list(scope.tenant, request), { retry: "safe-read" }),
  });
}

export function workspaceTemplateVersionsKey(scope: QueryScope, templateId: string) {
  return scopedKey(scope, "workspace-template", templateId, "versions");
}

export function workspaceTemplateVersionsQuery(
  service: Pick<WorkspaceTemplateService, "listVersions">,
  scope: QueryScope,
  templateId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: workspaceTemplateVersionsKey(scope, templateId),
    queryFn: scopedRead((request) => service.listVersions(scope.tenant, templateId, request), {
      retry: "safe-read",
    }),
  });
}
